import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import './CrmAutomationPage.css';

const crmImages = {
  hero: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/crm-and-automation-page/Hero%20Image.jpg',
  rapidLead: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/crm-and-automation-page/Rapid%20Lead%20Response.png',
  customerLtv: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/crm-and-automation-page/Customer%20LTV.png',
  dataConsolidation: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/crm-and-automation-page/Data%20Consolidation.png',
};

const services = [
  {
    id: '01',
    title: 'CRM Architecture',
    description:
      'We do not just set up software. We build a single source of truth from first click to closed deal.',
  },
  {
    id: '02',
    title: 'Autonomous Lead Nurturing',
    description:
      'Behavior-based email and SMS sequences keep your brand active without manual follow-ups.',
  },
  {
    id: '03',
    title: 'Workflow Engineering',
    description:
      'We automate repetitive operations across sales, marketing, and service tools.',
  },
  {
    id: '04',
    title: 'Data Intelligence & Hygiene',
    description:
      'Automated cleaning and deduplication rules keep reporting accurate and actionable.',
  },
];

const workCards = [
  {
    title: 'Rapid Lead Response',
    description:
      'Automated speed-to-lead triggers connect reps to prospects within seconds.',
    tag: 'Velocity . SalesOps . Automation',
  },
  {
    title: 'Customer LTV',
    description:
      'Post-purchase journeys, loyalty loops, and win-back automations for retention.',
    tag: 'Loyalty . D2C . Sequences',
  },
  {
    title: 'Data Consolidation',
    description:
      'Custom API bridges sync data across ERPs, CRMs, and marketing platforms.',
    tag: 'Engineering . Data . API',
  },
];

const testimonials = [
  {
    quote:
      'Automation is not about replacing people. It is about removing repetitive work so teams can focus on strategy and human connection.',
    title: 'On Human Potential',
  },
  {
    quote:
      'A funnel with holes is just a sieve. We build a seamless system that nurtures each lead with personal and timely communication.',
    title: 'On the Modern Funnel',
  },
  {
    quote:
      'In 2026, data is a core business asset. We turn passive records into active predictive intelligence.',
    title: 'On Data Integrity',
  },
];

const marqueeItems = [
  'HubSpot Elite',
  'Salesforce Integration',
  'Zapier Workflows',
  'Lead Scoring',
  'Customer Lifecycle Mapping',
  'Data Hygiene',
  'Predictive Churn',
];

export default function CrmAutomationPage() {
  return (
    <div className="crm-page">
      <nav className="crm-nav">
        <BrandLogo className="crm-nav__logo" size="nav" />
        <ul className="crm-nav__links">
          <li>
            <a href="#work">Success Profile</a>
          </li>
          <li>
            <a href="#services">System Logic</a>
          </li>
          <li>
            <a href="#about">Our Story</a>
          </li>
        </ul>
        <a href="#contact" className="crm-nav__cta">
          Contact
        </a>
      </nav>

      <section className="crm-hero">
        <div className="crm-hero__visual">
          <img
            src={crmImages.hero}
            alt="CRM and automation hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="crm-hero__content">
          <p className="crm-hero__eyebrow">Est. 2026 - London</p>
          <h1>
            Architects
            <br />
            <em>Of Autonomous</em>
            <br />
            Growth
          </h1>
          <p>
            Stop losing leads to manual follow-ups and fragmented data. BeyonDigital
            builds intelligent CRM ecosystems and automated workflows that handle the
            heavy lifting, so your team can focus on closing deals.
          </p>
          <div className="crm-hero__actions">
            <a href="#work" className="crm-btn crm-btn--dark">
              View Automation Flows
            </a>
            <a href="#contact" className="crm-btn crm-btn--ghost">
              Request Audit
            </a>
          </div>
          <div className="crm-proof">
            <div>
              <strong>65%</strong>
              <span>Reduction in manual tasks</span>
            </div>
            <div>
              <strong>3.5x</strong>
              <span>Lead velocity increase</span>
            </div>
            <div>
              <strong>24/7</strong>
              <span>Active engagement window</span>
            </div>
          </div>
        </div>
      </section>

      <section className="crm-marquee" aria-label="CRM tools marquee">
        <div className="crm-marquee__track">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              <span>{item}</span>
              <span className="crm-marquee__dot" aria-hidden="true">
                ·
              </span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="crm-section" id="services">
        <header className="crm-section__header">
          <h2>
            The <em>System Logic</em>
          </h2>
        </header>
        <div className="crm-grid crm-grid--services">
          {services.map((service) => (
            <article key={service.title} className="crm-card">
              <p className="crm-card__num">{service.id}</p>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="crm-section" id="work">
        <header className="crm-section__header">
          <h2>
            Success <em>Profiles:</em>
          </h2>
        </header>
        <div className="crm-grid crm-grid--work">
          {workCards.map((work, index) => (
            <article key={work.title} className="crm-work-card">
              <div className="crm-work-card__image">
                <img
                  src={
                    index === 0
                      ? crmImages.rapidLead
                      : index === 1
                        ? crmImages.customerLtv
                        : crmImages.dataConsolidation
                  }
                  alt={work.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="crm-work-card__body">
                <h3>{work.title}</h3>
                <p>{work.description}</p>
                <span>{work.tag}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="crm-section">
        <header className="crm-section__header">
          <h2>
            Automation
            <br />
            <em>Philosophy</em>
          </h2>
        </header>
        <div className="crm-grid crm-grid--quotes">
          {testimonials.map((item) => (
            <blockquote key={item.title} className="crm-quote">
              <span className="crm-quote__mark" aria-hidden="true">
                "
              </span>
              <p>"{item.quote}"</p>
              <cite>{item.title}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="crm-cta" id="contact">
        <div className="crm-cta__inner">
          <h2>
            Ready to scale without
            <br />
            <em>Stress?</em>
          </h2>
          <a className="crm-btn crm-btn--dark" href="mailto:sdevarsh54@beyondigital.co.uk">
            Request a system audit
          </a>
        </div>
      </section>

      <footer className="crm-footer" id="about">
        <div className="crm-footer__top">
          <div className="crm-footer__brand">
            <BrandLogo size="footer" asLink={false} variant="dark" />
            <p>
              Your business should run like clockwork. Let us build the systems
              that drive your growth.
            </p>
          </div>
          <div className="crm-footer__links">
            <div>
              <h4>Agency</h4>
              <a href="/">About</a>
              <a href="/">Work</a>
              <a href="/">Careers</a>
            </div>
            <div>
              <h4>Services</h4>
              <Link to="/services/social-media-marketing">Social Media</Link>
              <Link to="/services/seo">SEO</Link>
              <Link to="/services/website-generation">Websites</Link>
            </div>
            <div>
              <h4>Connect</h4>
              <a href="https://www.instagram.com/beyondigital.co/">Instagram</a>
              <a href="/">LinkedIn</a>
              <a href="/">Twitter/X</a>
            </div>
          </div>
        </div>
        <div className="crm-footer__bottom">London - India - Dubai</div>
      </footer>
    </div>
  );
}
